/**
 * Profile tab — edit your own name + change your password
 *
 * @module components/settings/ProfilePanel
 */

import React from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useIsPlatformMaster } from "@/hooks/useIsPlatformMaster";
import { isSupabaseConfigured } from "@/config/supabase";
import { ProfileNameForm } from "./ProfileNameForm";
import { ProfilePasswordForm } from "./ProfilePasswordForm";
import { useTranslation } from "react-i18next";

export const ProfilePanel: React.FC = () => {
  const { t } = useTranslation();
  const { session } = useAuth();
  const { isMaster, loading: masterLoading } = useIsPlatformMaster(!!session);
  const configured = isSupabaseConfigured();

  // `masterLoading ||` is not optional: isMaster starts at false while the RPC
  // is in flight (useIsPlatformMaster.ts). Gating on isMaster alone would hand
  // the master an editable form during that window — the same bug 996c5f1
  // fixed in Users.tsx: not knowing who the master is means not knowing this
  // isn't them.
  //
  // This is a slip guard, not a hostile-actor guard, unlike delete-user where
  // the real guard lives on the server: auth.updateUser() goes straight from
  // the browser to GoTrue with the user's own token — there is no route of
  // ours to gate the master on. A master can open devtools and call it
  // directly. If real enforcement is ever needed, that requires an
  // /api/profile route backed by service_role (accepted for now — spec §6.1).
  const locked = !configured || masterLoading || isMaster;

  const reason = !configured
    ? t('settings.profileDemoLocked')
    : isMaster
      ? t('settings.masterNotEditable')
      : undefined;

  return (
    <div className="space-y-4">
      {/* เหตุผลที่ล็อกบอกครั้งเดียวเหนือฟอร์ม — เดิมซ้ำในทั้งสองการ์ด */}
      {reason && (
        <p className="rounded-lg border border-neutral-200 bg-neutral-50 px-4 py-2.5 text-sm text-neutral-700">
          {reason}
        </p>
      )}
      {/* สองการ์ดเคียงกันบนจอกว้าง แทนเรียงยาวลงล่าง */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
        <ProfileNameForm
          firstName={session?.user.firstName ?? ""}
          lastName={session?.user.lastName ?? ""}
          disabled={locked || !session}
        />
        <ProfilePasswordForm disabled={locked || !session} />
      </div>
    </div>
  );
};

export default ProfilePanel;
